<script setup lang="ts">
import {Cpu, LayoutDashboard, RadioTower, ScrollText, Settings,} from "@lucide/vue";
import {computed} from "vue";
import {RouterLink, RouterView, useRoute} from "vue-router";

import ConnectionBadge from "@/components/ConnectionBadge.vue";
import {useDeviceStore} from "@/stores/deviceStore";
import {onlineLabel} from "@/utils/format";

defineProps<{
  booting: boolean;
  bootstrapError: string | null;
}>();

const route = useRoute();
const deviceStore = useDeviceStore();

const navItems = [
  {name: "dashboard", label: "控制台", icon: LayoutDashboard, to: "/"},
  {name: "devices", label: "设备", icon: Cpu, to: "/devices"},
  {name: "logs", label: "操作日志", icon: ScrollText, to: "/logs"},
  {name: "settings", label: "设置", icon: Settings, to: "/settings"},
] as const;

const currentLabel = computed(() => {
  const item = navItems.find((candidate) => candidate.name === route.name);
  return item?.label ?? "USB Relay Cloud";
});

// 登录路由引导期间保留 RouterView，使登录页的真实阶段进度条持续可见；
// 其他路由的启动加载行为保持不变。
const isLoginRoute = computed(() => route.name === "login");
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar" aria-label="主导航">
      <div class="brand-block">
        <span class="brand-icon"><RadioTower :size="22" /></span>
        <div>
          <strong>USB Relay Cloud</strong>
          <span>设备管理平台</span>
        </div>
      </div>

      <nav class="side-nav">
        <RouterLink
          v-for="item in navItems"
          :key="item.name"
          :to="item.to"
          class="nav-item"
          :class="{ active: route.name === item.name }"
        >
          <component :is="item.icon" :size="18" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div v-if="deviceStore.selectedDevice" class="sidebar-device">
        <span
          class="status-dot"
          :class="deviceStore.selectedDevice.onlineStatus.toLowerCase()"
        />
        <div>
          <strong>{{ deviceStore.selectedDevice.deviceName }}</strong>
          <span>{{ onlineLabel(deviceStore.selectedDevice.onlineStatus) }}</span>
        </div>
      </div>
    </aside>

    <div class="app-main">
      <header class="app-header">
        <div>
          <span class="header-kicker">USB Relay Platform</span>
          <h1>{{ currentLabel }}</h1>
        </div>
        <ConnectionBadge />
      </header>

      <div v-if="bootstrapError" class="alert alert-error" role="alert">
        {{ bootstrapError }}
      </div>

      <main
          class="page-stage"
          :class="{ 'is-booting': booting && !isLoginRoute }"
      >
        <div v-if="booting && !isLoginRoute" class="loading-line"/>
        <RouterView v-if="!booting || isLoginRoute"/>
      </main>
    </div>

    <nav class="bottom-nav" aria-label="移动端导航">
      <RouterLink
        v-for="item in navItems"
        :key="item.name"
        :to="item.to"
        class="bottom-nav-item"
        :class="{ active: route.name === item.name }"
      >
        <component :is="item.icon" :size="20" />
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>
  </div>
</template>
